import * as React from 'react'
import {ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {PageFooterArea, PageFotterAreaLeft, PageMainArea} from '../shared/hoc/withAdminPageTemplate'
import {StyledTableCaption, TableRowName} from '../../styledComponents/adminPage/Table'
import {EquipmentsRepository} from './EquipmentsRepository'
import {Nullable} from '../../app/TypeHelpers'
import {BackLink, DeleteLink} from '../../styledComponents/adminPage/Links'
import {HeadingAreaMessageParams} from '../shared/pageBaseComponents/HeadingAreaMessageComponent'
import {Messages} from '../../Messages'
import {MESSAGE_TYPE} from '../../CodeMaster'
import {AdminDefaultMediumButton} from '../../styledComponents/adminPage/Buttons'
import {RowFlexContainer} from '../../styledComponents/FlexComponents'
import {Box, Table, TableBody, TableCell, TableRow} from '@mui/material'
import Equipment from './Equipment'
import PreformattedDiv from '../../styledComponents/PreformattedDiv'
import {judgeAuthorityCheck} from '../../__NoAutomatedTests/common/util/Natutil'
import styled from 'styled-components'
import {Location} from 'react-router-dom'

interface Props {
    equipmentsRepo: EquipmentsRepository
    condominiumCd: string
    equipmentId: number
    location: Location
}

interface State {
    equipment: Nullable<Equipment>
    editMode: boolean
}

class AdminEquipmentDetailsPage extends React.Component<Props & AdminPagePropsSet, State> {

    static displayName: string = 'AdminEquipmentDetailsPage'

    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)
        this.state = {
            equipment: null,
            editMode: false,
        }

        this.handleEditButtonClick = this.handleEditButtonClick.bind(this)
        this.handleDeleteButtonClick = this.handleDeleteButtonClick.bind(this)
        this.handleDeleteOkButtonClick = this.handleDeleteOkButtonClick.bind(this)
        this.handleDeleteCancelButtonClick = this.handleDeleteCancelButtonClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const editMode: boolean = judgeAuthorityCheck('EDIT-EQUIPMENT_FACILITY')
            const {equipmentsRepo, condominiumCd, equipmentId} = this.props

            const equipment: Nullable<Equipment> = await this.props.asyncCallFunction(
                equipmentsRepo.getEquipmentById.bind(equipmentsRepo, condominiumCd, equipmentId),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
            if (equipment) {
                this.setState({
                    equipment: equipment,
                    editMode: editMode,
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }

        this.showInitialMessage()
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    handleEditButtonClick(): void {
        const {condominiumCd, equipmentId} = this.props
        this.props.navigate(`/admin/condominiums/${condominiumCd}/equipments/${equipmentId}/edit`)
    }

    handleDeleteButtonClick(): void {
        if (this.state.equipment === null) {
            return
        }
        this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0011.errorLevel,
                message: Messages.IOPCC0011.toString(this.state.equipment.name),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleDeleteCancelButtonClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleDeleteOkButtonClick,
                    },
                ],
            },
        )
    }

    handleDeleteCancelButtonClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleDeleteOkButtonClick(): Promise<void> {
        this.props.eventEmitter.closeDialog()

        const {equipmentsRepo, condominiumCd, equipmentId} = this.props

        let succeed: Nullable<boolean> = false
        await this.props.eventEmitter.showLoading()
        try {
            succeed = await this.props.asyncCallFunction(
                equipmentsRepo.deleteEquipment.bind(equipmentsRepo, condominiumCd, equipmentId),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        if (!succeed) {
            return
        }

        const equipmentName = this.state.equipment ? this.state.equipment.name : ''
        this.props.navigate(
            `/admin/condominiums/${condominiumCd}/equipments`,
            {
                state: {
                    initialMessage: {
                        title: null,
                        messageLevel: MESSAGE_TYPE.INFORMATION,
                        detail: Messages.IOPCC0006.toString('設備', equipmentName),
                    },
                },
            },
        )
    }

    renderPageActionArea(): ReactNode {
        if (this.state.equipment === null) {
            return
        }
        if (!this.state.editMode) {
            return
        }
        return (
            <>
                <Box marginTop={'10px'}>
                    <RowFlexContainer>
                        <AdminDefaultMediumButton
                            onClick={this.handleEditButtonClick}>設備編集</AdminDefaultMediumButton>
                        <DeleteLink onClick={this.handleDeleteButtonClick}>設備を削除する</DeleteLink>
                    </RowFlexContainer>
                </Box>
            </>
        )
    }

    renderNullableCount(count: Nullable<number>, suffix: string): ReactNode {
        if (count === null) {
            return
        }
        return (
            <div>{count} {suffix}</div>
        )
    }

    renderExtraEquipmentInfo(): ReactNode {
        if (!this.state.equipment) {
            return
        }
        return (
            <>
                <TableRow>
                    <TableRowName>予約可能回数</TableRowName>
                    <TableCell>
                        {this.renderNullableCount(this.state.equipment.reservationCountMonthly, '回/月')}
                        {this.renderNullableCount(this.state.equipment.reservationCountDaily, '回/日')}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableRowName>抽選申込数</TableRowName>
                    <TableCell>
                        {this.renderNullableCount(this.state.equipment.lotteryEntryCountMonthly, '件/月')}
                    </TableCell>
                </TableRow>
            </>
        )
    }

    renderDetailsTable(): ReactNode {
        if (this.state.equipment === null) {
            return
        }
        return (
            <>
                <Box marginTop={'10px'}>
                    <Table>
                        <StyledTableCaption>設備情報</StyledTableCaption>
                        <TableBody>
                            <TableRow>
                                <TableRowName>設備名</TableRowName>
                                <TableCell>
                                    {this.state.equipment.name}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableRowName>説明</TableRowName>
                                <TableCell>
                                    <ExplainText>
                                        {this.state.equipment.explainText}
                                    </ExplainText>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableRowName>親設備</TableRowName>
                                <TableCell>
                                    {this.state.equipment.parentEquipmentName || ''}
                                </TableCell>
                            </TableRow>
                            {this.renderExtraEquipmentInfo()}
                        </TableBody>
                    </Table>
                </Box>
            </>
        )
    }

    renderPageFooterArea(): ReactNode {
        return (
            <>
                <PageFotterAreaLeft>
                    <BackLink
                        to={`/admin/condominiums/${this.props.condominiumCd}/equipments`}
                    >
                        設備一覧へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </>
        )
    }

    render(): ReactNode {
        return (
            <>
                {this.renderPageActionArea()}
                <PageMainArea>
                    {this.renderDetailsTable()}
                </PageMainArea>

                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

const ExplainText = styled(PreformattedDiv)`
  word-break: break-all;
`

export default createAdminPage(AdminEquipmentDetailsPage)
